import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { LeadCycleApiService } from '../services/lead-cycle-api.service';
import { LeadCycleResponse } from '../models/lead-cycle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../shared/services/permission.service';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-lead-cycle-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    DragDropModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    HasPermissionDirective
  ],
  templateUrl: './lead-cycle-list.component.html',
  styleUrls: ['./lead-cycle-list.component.css']
})
export class LeadCycleListComponent implements OnInit {
  ciclos: LeadCycleResponse[] = [];
  loading = false;
  error = '';
  /** Reorden en vuelo: evita drags encadenados mientras el POST no confirma. */
  reordering = false;

  constructor(
    private cycleApi: LeadCycleApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private permissions: PermissionService
  ) { }

  /**
   * `POST /api/lead-cycles/reorder` exige `settings:EDIT` (LeadCycleController.MODULE = "settings"),
   * no `leads:edit` como los botones Editar/Eliminar de esta misma tabla. Ese desajuste es
   * preexistente y afecta a los botones, no al arrastre: aquí se consulta el permiso real del endpoint.
   */
  get canReorder(): boolean {
    return this.permissions.has('settings', 'edit');
  }

  ngOnInit(): void {
    this.cargarCiclos();
  }

  cargarCiclos(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.cycleApi.getCycles(businessId).subscribe({
      next: (data) => {
        this.ciclos = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los ciclos de lead';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  /**
   * Reordena por drag & drop. La lista no pagina (size=100), así que `ciclos` es SIEMPRE
   * la lista completa del negocio y se puede enviar entera al endpoint de reorden.
   *
   * Estrategia optimista: se mueve la fila y se renumera la columna "Orden" (1..N) al instante;
   * si la API falla se revierte el orden y el displayOrder exactos que había antes.
   * Sin toast en el éxito (regla de drag-drop del skill crm-toast-notifications).
   */
  dropCiclo(event: CdkDragDrop<LeadCycleResponse[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    if (!this.canReorder || this.reordering) return;

    // Snapshot para revertir: conserva posición Y displayOrder previos de cada ítem.
    const snapshot = this.ciclos.map(c => ({ item: c, displayOrder: c.displayOrder }));

    moveItemInArray(this.ciclos, event.previousIndex, event.currentIndex);
    this.ciclos.forEach((c, idx) => c.displayOrder = idx + 1);

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const orderedIds = this.ciclos.map(c => c.id);

    this.reordering = true;
    this.cycleApi.reorderCycles(businessId, orderedIds).subscribe({
      next: () => {
        this.reordering = false;
      },
      error: (err) => {
        this.reordering = false;
        // Revertir: la UI no puede quedar mostrando un orden que el backend rechazó.
        this.ciclos = snapshot.map(s => {
          s.item.displayOrder = s.displayOrder;
          return s.item;
        });
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al reordenar los ciclos de lead'
        });
      }
    });
  }

  nuevoCiclo(): void {
    this.router.navigate(['/config-leads/ciclos/crear']);
  }

  editarCiclo(ciclo: LeadCycleResponse): void {
    this.router.navigate(['/config-leads/ciclos/editar', ciclo.id]);
  }

  async eliminarCiclo(ciclo: LeadCycleResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar ciclo',
      message: `¿Está seguro de eliminar el ciclo "${ciclo.name}"?`
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.cycleApi.deleteCycle(ciclo.id, businessId).subscribe({
      next: () => {
        this.cargarCiclos();
        this.toast.deleted('Ciclo de lead', `Se eliminó "${ciclo.name}" correctamente.`);
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el ciclo' });
      }
    });
  }

  getTriggerColor(triggerAction: string): BadgeColor {
    switch (triggerAction) {
      case 'CREATE_DEAL': return 'success';
      case 'CLOSE_LEAD': return 'error';
      default: return 'dark';
    }
  }

  getTriggerLabel(triggerAction: string): string {
    switch (triggerAction) {
      case 'CREATE_DEAL': return 'Crear Deal';
      case 'CLOSE_LEAD': return 'Cerrar Lead';
      default: return 'Ninguna';
    }
  }

  getActiveColor(isActive: boolean): BadgeColor {
    return isActive ? 'success' : 'error';
  }

  getActiveLabel(isActive: boolean): string {
    return isActive ? 'Activo' : 'Inactivo';
  }
}
